import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect, useDispatch } from 'react-redux';
import injectSaga from 'utils/injectSaga';
import Loader from 'components/Loader';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';
import { Button, H2, Div, Img } from 'components/Ui';
import { addPromotion, removePromotion } from 'containers/Cart/actions';
import { times } from 'lodash';
import { useIsMobile } from 'utils/hooks';
import { dataLayerPush } from 'utils/tracking';
import { getToUrl } from 'utils/translation';
import { push } from 'connected-react-router';
import { Bottle, Cooler, PromotionIcon } from 'images';
import styles from './styles.scss';
import {
  selectPromotionAssets,
  selectIsPromoAssetsFetching,
} from './selectors';
import { fetchPromotionAssets } from './actions';
import promotionDetailSaga from './saga';
import { PROMOTION_DETAILS_KEY } from './constants';
import * as content from './content';
import { selectPromotionLandingPageData } from '../Landing/selectors';

const PromotionDetails = ({
  isPromoAssetsFetching,
  fetchPromoAssets,
  validatePromotion,
  addPromotionToCart,
  removeCartPromotion,
  onClickAction,
  bottle5GCount,
  hasCartDispenser,
  viewCart,
  onNext,
  showPromotionRedeem,
  promotionLandingPageData,
  // goNext,
}) => {
  useEffect(() => {
    fetchPromoAssets();
  }, []);

  const promoContent =
    content.promoContent.filter(
      (item) => item.name === promotionLandingPageData.path,
    )[0] || content;

  const contentHeader = useMemo(
    () =>
      validatePromotion ? promoContent.validateHeader : promoContent.learn,
    [validatePromotion, promoContent],
  );
  const dispatch = useDispatch();
  const onReject = viewCart
    ? () => dispatch(push(getToUrl(`/cart`, 'U31')))
    : onNext;
  const onRemovePromotion = () => {
    onReject();
    onClickAction();
    removeCartPromotion();
  };

  const hasPromoInitiated = hasCartDispenser || bottle5GCount > 0;
  const isEnoughBottle5G = bottle5GCount >= promoContent.bottleCount;
  const remaining5GQuantity = promoContent.bottleCount - bottle5GCount;
  const hasPromoStarted = validatePromotion && hasPromoInitiated;
  const headerTitle = hasPromoStarted
    ? contentHeader.promoInitiatedTitle
    : contentHeader.title;
  const acceptButtonLabel = hasPromoStarted
    ? 'UPDATE ORDER'
    : promoContent.acceptButtonLabel;

  const bottleValidationText =
    !isEnoughBottle5G &&
    (bottle5GCount === 0
      ? 'Add 3 or more bottles'
      : remaining5GQuantity === 2
      ? 'Add 2 more bottles'
      : 'Add 1 more bottle');

  const equipmentValidationText =
    promoContent.dispenserCount > 0 &&
    !hasCartDispenser &&
    (bottleValidationText
      ? '(and a water dispenser)'
      : 'Add a water dispenser to your order');

  const isMobile = useIsMobile();

  const onAddPromotion = () => {
    const path = window.location.href;
    const quickShopPromo = path.includes('/quick-shop/');
    if (typeof showPromotionRedeem === 'function') {
      if (quickShopPromo) {
        // mmm ppp promotionClick - Done dlA12 - Quick Shop > Learn More > Promotion Dialog > Redeem
        dataLayerPush(
          'Acquisition',
          {
            event: 'promotionClick',
            ecommerce: {
              promoClick: {
                promotions: [
                  {
                    name: promotionLandingPageData.promotionName,
                    position: 'Quick Shop Promo Banner',
                  },
                ],
              },
            },
          },
          'dlA12',
          'PUSH_ADD_PROMOTION_DATA',
          showPromotionRedeem,
        );
      } else {
        // mmm ppp promotionClick - Done dlA13 - Home > Learn More > Promotion Dialog > Redeem
        dataLayerPush(
          'Acquisition',
          {
            event: 'promotionClick',
            ecommerce: {
              promoClick: {
                promotions: [
                  {
                    name: promotionLandingPageData.promotionName,
                    position: 'Top Promotion Banner',
                  },
                ],
              },
            },
          },
          'dlA13',
          'PUSH_ADD_PROMOTION_DATA',
          showPromotionRedeem,
        );
      }
    }
    addPromotionToCart();
    onClickAction();
    dispatch(push(getToUrl('/quick-shop/select-water', 'U32')));
  };

  const render5GQuantityIndicators = () => (
    <div className={styles.quantityIndicators}>
      {times(
        isEnoughBottle5G ? promoContent.bottleCount : bottle5GCount,
        () => (
          <span className={`${styles.quantityIndicator} ${styles.filled}`} />
        ),
      )}
      {times(remaining5GQuantity, () => (
        <span className={styles.quantityIndicator} />
      ))}
    </div>
  );

  return (
    <div className={styles.promotionDetails}>
      <Loader loading={isPromoAssetsFetching}>
        <div className={styles.header}>
          {contentHeader.showIcon && !validatePromotion && (
            <div className={styles.discountIcon}>
              <img src={PromotionIcon} alt="" />
            </div>
          )}
          <H2 className={styles.title}>{headerTitle}</H2>
          <Div className={styles.note}>{contentHeader.note}</Div>
        </div>
        <div className={styles.details}>
          <div className={styles.promoItem}>
            <div className={styles.image}>
              <Img src={Bottle} alt="5 Gallon Bottle" />
              {validatePromotion && render5GQuantityIndicators()}
            </div>
            <div className={isMobile ? styles.quantityMobile : styles.quantity}>
              x 3
            </div>
          </div>
          {promoContent.dispenserCount > 0 && (
            <>
              <div className={styles.operator}>+</div>
              <div className={styles.promoItem}>
                <div className={styles.image}>
                  <Img src={Cooler} alt="Equipment" />
                  {validatePromotion && (
                    <div className={styles.quantityIndicators}>
                      <span
                        className={`${styles.quantityIndicator} ${
                          hasCartDispenser && styles.filled
                        }`}
                      />
                    </div>
                  )}
                </div>
                <div
                  className={isMobile ? styles.quantityMobile : styles.quantity}
                >
                  x 1
                </div>
              </div>
            </>
          )}
          <div className={styles.result}>
            <div className={styles.operator}>=</div>
            <Div className={isMobile ? styles.discountMobile : styles.discount}>
              {
                promotionLandingPageData.variables.filter(
                  (v) => v.name === '{offer}',
                )[0].value
              }
            </Div>
          </div>
        </div>
        {validatePromotion && (
          <div className={styles.validateFooter}>
            <Div className={styles.title}>
              {promoContent.validateFooter.title}
            </Div>
            <div className={styles.noteContainer}>
              <Div className={styles.note}>{bottleValidationText}</Div>
              <Div className={styles.note}>{equipmentValidationText}</Div>
            </div>
          </div>
        )}
        <div className={styles.buttonRow}>
          <Button
            className={styles.rejectButton}
            text={promoContent.rejectButtonLabel}
            onClick={onRemovePromotion}
          />
          <Button
            className={styles.acceptButton}
            text={acceptButtonLabel}
            onClick={onAddPromotion}
          />
        </div>
        <Div className={styles.terms}>{promoContent.termsNote}</Div>
      </Loader>
    </div>
  );
};

PromotionDetails.propTypes = {
  fetchPromoAssets: PropTypes.func.isRequired,
  isPromoAssetsFetching: PropTypes.bool,
  validatePromotion: PropTypes.bool,
  addPromotionToCart: PropTypes.func.isRequired,
  removeCartPromotion: PropTypes.func.isRequired,
  onClickAction: PropTypes.func,
  bottle5GCount: PropTypes.number,
  hasCartDispenser: PropTypes.bool,
  viewCart: PropTypes.bool,
  onNext: PropTypes.func,
  showPromotionRedeem: PropTypes.func,
  promotionLandingPageData: PropTypes.object,
  // goNext: PropTypes.bool,
};

const mapStateToProps = createStructuredSelector({
  promotionAssets: selectPromotionAssets(),
  isPromoAssetsFetching: selectIsPromoAssetsFetching(),
  promotionLandingPageData: selectPromotionLandingPageData(),
});

const mapDispatchToProps = {
  fetchPromoAssets: fetchPromotionAssets,
  addPromotionToCart: addPromotion,
  removeCartPromotion: removePromotion,
};

const withSaga = injectSaga({
  key: PROMOTION_DETAILS_KEY,
  saga: promotionDetailSaga,
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withSaga,
)(PromotionDetails);
